import React, { useEffect, useState } from "react";
import { useLocation, useOutlet } from "react-router-dom";
import Header from "./header/Header";
import { pages } from "../general.models";
import useDeviceDetection from "../hooks/useDeviceDetection";
import ReactLenis from "lenis/react";
import { AnimatePresence, motion } from "framer-motion";
import ScrollArrow from "./scroll-to-top-arrow";
import useIsRTL from "../hooks/use-is-rtl";


const ControlPanel = ({ manualVW, setManualVW, maxPageWidth, setMaxPageWidth }) => {
    // Helper function to update CSS variable when the value changes.
    const updateCSSVariable = (name, value) => {
        document.documentElement.style.setProperty(name, value);
    };

    const handleVWChange = (e) => {
        const newVW = parseFloat(e.target.value);
        setManualVW(newVW);
        // Update the --vw CSS variable using the new value.
        updateCSSVariable("--vw", newVW);
    };

    const handleMaxPageWidthChange = (e) => {
        const newWidth = e.target.value;
        setMaxPageWidth(newWidth);
        // Append "px" if you want to work with pixel values.
        updateCSSVariable("--max-page-width", `${newWidth}px`);
    };

    return (
        <div className="control-panel" style={controlPanelStyle}>
            <h3>Control Panel</h3>
            <div style={fieldStyle}>
                <label htmlFor="vwInput">Viewport Width Factor (vw):</label>
                <input
                    id="vwInput"
                    type="number"
                    step="0.01"
                    min="0"
                    max="1.3"
                    value={manualVW}
                    onChange={handleVWChange}
                />
            </div>
            <div style={fieldStyle}>
                <label htmlFor="maxPageWidth">Max Page Width (px):</label>
                <input
                    id="maxPageWidth"
                    type="number"
                    step="1"
                    value={maxPageWidth}
                    onChange={handleMaxPageWidthChange}
                />
            </div>
        </div>
    );
};

// Basic inline styling for demonstration purposes.
const controlPanelStyle = {
    position: "fixed",
    top: "20px",
    right: "20px",
    background: "#fff",
    border: "1px solid #ccc",
    padding: "1rem",
    zIndex: 1000,
    boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
    width: "500px"
};

const fieldStyle = {
    marginBottom: "0.5rem",
    width: "max-content",
};

// export default ControlPanel;

function useViewportSize() {
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

    useEffect(() => {
        function handleResize() {
            setViewportHeight(window.innerHeight);
            setViewportWidth(window.innerWidth);
        }

        // Initial call on mount
        handleResize();
        // Listen for resize
        window.addEventListener("resize", handleResize);

        // Cleanup listener on unmount
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return { viewportHeight, viewportWidth };
}

export default function Layout() {
    const titleMap = pages;
    const [t, isRtl] = useIsRTL();
    const currentLocation = useLocation();
    const deviceType = useDeviceDetection();
    const reloadState = useState(() => {
        const savedValue = document.cookie
            .split('; ')
            .find((row) => row.startsWith('showIntro='))
            ?.split('=')[1];
        return savedValue ? JSON.parse(savedValue) : true;
    });
    const { viewportHeight, viewportWidth } = useViewportSize();
    const [relativeVh, setVh] = useState(1);
    const outlet = useOutlet({ reloadState, relativeVh });

    // States for control panel
    const [showControlPanel, setShowControlPanel] = useState(false);
    // Initialize manualVW with the computed vw, but override later if needed.
    //   const computedVW = viewportWidth / 1555;
    //   const initialVW = computedVW > 1 ? computedVW : 1;
    const [manualVW, setManualVW] = useState(0.89);
    const [maxPageWidth, setMaxPageWidth] = useState(1440); // Default max page width in px

    useEffect(() => {
        // Convert screen size into “1% of viewport height/width” in px
        const vw = viewportWidth / 1550;
        const vh = viewportHeight / 875;

        document.documentElement.style.setProperty("--vh", `${vh}`);
        // Only override if vw is greater than 0.95; otherwise, use the computed value.
        document.documentElement.style.setProperty("--vw", `${vw > 1 ? manualVW : (viewportWidth > 768 ? vw * 0.8 : vw)}`);
        document.documentElement.style.setProperty("--max-page-width", `${vw > 1 ? '1200px' : '980px'}`);
        document.documentElement.style.setProperty("--vh-mobile", `${vh}`);
        document.documentElement.style.setProperty("--vw-mobile", `${vw}`);
        setVh(vh);
    }, [viewportHeight, viewportWidth, manualVW]);

    useEffect(() => {
        const curTitle = titleMap.find((item) => item.linkTo === currentLocation.pathname);

        document.title =
            currentLocation.pathname.substring(1)?.length
                ? `${isRtl ? "\u202B" : ""}LNET & GADA | ${t(curTitle?.text)}`
                : "LNET | GADA";
    }, [currentLocation, t, titleMap]);

    //#region Accessorise for developing and debugging

    // When 'value' changes, save it to session storage
    useEffect(() => {
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 7); // Set expiration to one week from now
        document.cookie = `showIntro=${JSON.stringify(reloadState[0])}; expires=${expirationDate.toUTCString()}; path=/`;
    }, [reloadState[0]]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Listen for Alt+P to toggle the control panel visibility.
            if (e.altKey && e.code === "KeyP") {
                setShowControlPanel((prev) => !prev);
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    //#endregion

    return (
        <>
            {/* Render the control panel if toggled on */}
            {showControlPanel && (
                <ControlPanel
                    manualVW={manualVW}
                    setManualVW={setManualVW}
                    maxPageWidth={maxPageWidth}
                    setMaxPageWidth={setMaxPageWidth}
                />
            )}

            <div className={`site-wrapper ${deviceType?.isMobile && "mobile"} ${deviceType?.isTablet && "tablet"}`}>
                <Header />
                <ReactLenis
                    root
                    options={{
                        duration: 1.9,
                        overscroll: false,
                        touchMultiplier: 0.4,
                        prevent: (node) =>
                            node.className === "backdrop" || node.className === "gefen-programs-backdrop",
                        easing: (x) => 1 - Math.pow(1 - x, 3),
                    }}
                >
                    <main className="with-lennis">
                        {outlet && (
                            <motion.div>
                                {outlet}
                            </motion.div>
                        )}
                       </main>
                    <ScrollArrow />
                </ReactLenis>

            </div>
        </>
    );
}
